import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaPen } from 'react-icons/fa';
import { Badge, Button, LabelChip, SortHeader } from '../shared/ui';
import { PopupContext } from '../App';
import StudentEditorModal from '../components/directory/StudentEditorModal';
import FamilyEditorModal from '../components/directory/FamilyEditorModal';
import { API_URL } from '../config/env';
import { getAllVoucherStatusNotesForStudent } from '../services/voucherStatusNotesService';
import { fetchStudentById, fetchStudentVoucherHistory } from '../services/studentDetailService';
import styles from './StudentDetailPage.module.css';

function authHeaders(extra = {}) {
  return { Authorization: `Bearer ${localStorage.getItem('token')}`, ...extra };
}

function formatMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return `$${n.toFixed(2)}`;
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

export default function StudentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const flash = useContext(PopupContext);
  const [student, setStudent] = useState(null);
  const [vouchers, setVouchers] = useState([]);
  const [notes, setNotes] = useState([]);
  const [labels, setLabels] = useState([]);
  const [familiesLookup, setFamiliesLookup] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [secondaryWarning, setSecondaryWarning] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [familyModalOpen, setFamilyModalOpen] = useState(false);
  const [pendingFamilyId, setPendingFamilyId] = useState(null);
  const [voucherSort, setVoucherSort] = useState('');
  const [voucherDir, setVoucherDir] = useState('');
  const [reactivating, setReactivating] = useState(false);

  const refreshFamiliesLookup = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/families?size=500&status=all&sort=last_name`, {
        headers: authHeaders()
      });
      const d = await r.json();
      setFamiliesLookup(Array.isArray(d) ? d : d?.data || []);
    } catch {
      setFamiliesLookup([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setSecondaryWarning('');
    setVoucherSort('');
    setVoucherDir('');
    try {
      const sid = parseInt(id, 10);
      if (Number.isNaN(sid)) throw new Error('Invalid student');

      const stu = await fetchStudentById(sid);
      setStudent(stu);

      const extId = stu.student_id != null ? String(stu.student_id).trim() : '';

      const results = await Promise.allSettled([
        fetchStudentVoucherHistory(sid),
        getAllVoucherStatusNotesForStudent(sid, extId || null),
        fetch(`${API_URL}/api/labels?include_counts=1`, { headers: authHeaders() }).then((r) =>
          r.ok ? r.json() : []
        )
      ]);

      const warn = [];
      const vRes = results[0];
      if (vRes.status === 'fulfilled') {
        const voucherPayload = vRes.value;
        const vList = Array.isArray(voucherPayload?.vouchers)
          ? voucherPayload.vouchers
          : Array.isArray(voucherPayload)
            ? voucherPayload
            : [];
        setVouchers(vList);
      } else {
        setVouchers([]);
        warn.push('Could not load voucher transactions.');
      }

      const nRes = results[1];
      if (nRes.status === 'fulfilled') {
        const noteRows = nRes.value;
        setNotes(Array.isArray(noteRows) ? noteRows : []);
      } else {
        setNotes([]);
        warn.push('Could not load voucher notes.');
      }

      const lRes = results[2];
      if (lRes.status === 'fulfilled') {
        const labRes = lRes.value;
        setLabels(Array.isArray(labRes) ? labRes : []);
      } else {
        setLabels([]);
      }

      if (warn.length) setSecondaryWarning(warn.join(' '));
    } catch (e) {
      setError(e.message || 'Failed to load');
      setStudent(null);
      setVouchers([]);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void refreshFamiliesLookup();
  }, [refreshFamiliesLookup]);

  const onVoucherSort = useCallback(({ sort, dir }) => {
    setVoucherSort(sort);
    setVoucherDir(dir);
  }, []);

  const sortedVouchers = useMemo(() => {
    if (!voucherSort) return vouchers;
    const dirMult = voucherDir === 'desc' ? -1 : 1;
    const copy = [...vouchers];
    copy.sort((a, b) => {
      let cmp = 0;
      switch (voucherSort) {
        case 'total': {
          const na = Number(a.total);
          const nb = Number(b.total);
          const va = Number.isNaN(na) ? 0 : na;
          const vb = Number.isNaN(nb) ? 0 : nb;
          cmp = va - vb;
          break;
        }
        case 'date_range':
          cmp = String(a.date_range || '').localeCompare(String(b.date_range || ''), undefined, {
            sensitivity: 'base'
          });
          break;
        case 'settlement_date': {
          const da = a.settlement_date ? new Date(a.settlement_date).getTime() : 0;
          const db = b.settlement_date ? new Date(b.settlement_date).getTime() : 0;
          const na = Number.isNaN(da) ? 0 : da;
          const nb = Number.isNaN(db) ? 0 : db;
          cmp = na - nb;
          break;
        }
        case 'location_name':
          cmp = String(a.location_name || '').localeCompare(String(b.location_name || ''), undefined, {
            sensitivity: 'base'
          });
          break;
        case 'agreement_number':
          cmp = String(a.agreement_number || '').localeCompare(String(b.agreement_number || ''), undefined, {
            numeric: true,
            sensitivity: 'base'
          });
          break;
        default:
          break;
      }
      if (cmp === 0) {
        const ia = Number(a.id) || 0;
        const ib = Number(b.id) || 0;
        cmp = ia - ib;
      }
      return dirMult * (cmp > 0 ? 1 : cmp < 0 ? -1 : 0);
    });
    return copy;
  }, [vouchers, voucherSort, voucherDir]);

  const refreshLabelsOnly = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/labels?include_counts=1`, { headers: authHeaders() });
      if (!r.ok) return;
      const labRes = await r.json();
      if (Array.isArray(labRes)) setLabels(labRes);
    } catch {
      /* ignore */
    }
  }, []);

  const reactivateStudent = async () => {
    if (!student) return;
    setReactivating(true);
    try {
      const res = await fetch(`${API_URL}/api/students/${student.id}`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          first_name: student.first_name,
          last_name: student.last_name,
          dob: student.dob,
          is_active: true
        })
      });
      if (!res.ok) throw new Error('Failed to reactivate');
      const body = await res.json();
      const fam = student.family;
      setStudent({ ...body, family: fam, labels: body.labels ?? student.labels });
      flash('Student reactivated');
    } catch (e) {
      flash(e.message || 'Failed to reactivate', 'error');
    } finally {
      setReactivating(false);
    }
  };

  if (loading && !student) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Loading student…</div>
      </div>
    );
  }

  if (error || !student) {
    return (
      <div className={styles.page}>
        <button type="button" className={styles.backBtn} onClick={() => navigate('/students')}>
          <FaArrowLeft aria-hidden /> Back to students
        </button>
        <div className={styles.errorBox}>{error || 'Student not found'}</div>
      </div>
    );
  }

  const fam = student.family;
  const lastName = student.last_name || fam?.last_name || '';
  const fullName = `${student.first_name || ''} ${lastName}`.trim();

  return (
    <div className={styles.page}>
      <div className={styles.backRow}>
        <button type="button" className={styles.backBtn} onClick={() => navigate('/students')}>
          <FaArrowLeft aria-hidden /> Students
        </button>
      </div>

      {secondaryWarning ? <div className={styles.warnBanner}>{secondaryWarning}</div> : null}

      {!student.is_active ? (
        <div className={styles.inactiveBanner} role="status">
          <div className={styles.inactiveBannerText}>
            <strong>This student is inactive.</strong>
            <span>Inactive profiles are omitted from the default directory until reactivated.</span>
          </div>
          <Button type="button" variant="secondary" disabled={reactivating} onClick={reactivateStudent}>
            Reactivate
          </Button>
        </div>
      ) : null}

      <header className={styles.hero}>
        <div>
          <h1 className={styles.heroTitle}>{fullName || 'Student'}</h1>
          <p className={styles.heroMeta}>
            Directory profile
            {!student.is_active ? ' · Inactive' : ''}
          </p>
          {student.student_id ? (
            <span className={styles.idBadge}>CIN {student.student_id}</span>
          ) : null}
          <div className={styles.chipRow}>
            {(student.labels || []).map((l) => (
              <LabelChip key={l.id} label={l.name} color={l.color} />
            ))}
          </div>
        </div>
        <Button type="button" variant="secondary" onClick={() => setEditorOpen(true)}>
          <FaPen aria-hidden /> Edit
        </Button>
      </header>

      <div className={styles.mainGrid}>
        <div className={`${styles.card} ${styles.cardMuted}`}>
          <h2 className={styles.cardTitle}>Profile</h2>
          <p className={styles.cardHint}>Identity and household link</p>
          <dl className={styles.detailGrid}>
            <div className={styles.detailRow}>
              <dt>Date of birth</dt>
              <dd>{formatDate(student.dob)}</dd>
            </div>
            <div className={styles.detailRow}>
              <dt>Status</dt>
              <dd>
                <Badge variant={student.is_active ? 'success' : 'neutral'}>
                  {student.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </dd>
            </div>
            <div className={styles.detailRow}>
              <dt>Family</dt>
              <dd>
                {fam ? (
                  <button
                    type="button"
                    className={styles.familyLink}
                    onClick={() => navigate(`/families/${fam.id}`)}
                  >
                    {fam.first_name} {fam.last_name} · #{fam.case_number}
                  </button>
                ) : (
                  <span className={styles.muted}>—</span>
                )}
              </dd>
            </div>
          </dl>
        </div>

        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Voucher notes (all periods)</h2>
          <p className={styles.cardHint}>Closure and period notes from the voucher workspace</p>
          {notes.length === 0 ? (
            <p className={styles.empty}>No notes yet.</p>
          ) : (
            <div className={styles.noteList}>
              {notes.map((n) => (
                <div key={n.id} className={styles.noteCard}>
                  <div className={styles.noteMeta}>
                    {[n.settlement_period, n.date_range].filter(Boolean)[0] || 'Period —'}
                    {n.full_name ? ` · ${n.full_name}` : ''}
                    {n.created_at ? ` · ${new Date(n.created_at).toLocaleString()}` : ''}
                  </div>
                  <p className={styles.noteBody}>{n.note}</p>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className={`${styles.card} ${styles.spanFull}`}>
          <h2 className={styles.cardTitle}>Voucher transactions</h2>
          <p className={styles.cardHint}>
            All imported lines matching this profile (by linked record or agreement student ID)
          </p>
          {vouchers.length === 0 ? (
            <p className={styles.empty}>No vouchers on file.</p>
          ) : (
            <div
              className={styles.tableWrap}
              role="region"
              aria-label="Voucher transactions"
              tabIndex={0}
            >
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.thNum}>
                      <SortHeader
                        column="total"
                        label="Amount"
                        currentSort={voucherSort}
                        currentDir={voucherDir}
                        onSort={onVoucherSort}
                        align="right"
                      />
                    </th>
                    <th>
                      <SortHeader
                        column="date_range"
                        label="Period"
                        currentSort={voucherSort}
                        currentDir={voucherDir}
                        onSort={onVoucherSort}
                      />
                    </th>
                    <th>
                      <SortHeader
                        column="settlement_date"
                        label="Settlement"
                        currentSort={voucherSort}
                        currentDir={voucherDir}
                        onSort={onVoucherSort}
                      />
                    </th>
                    <th>
                      <SortHeader
                        column="location_name"
                        label="Location"
                        currentSort={voucherSort}
                        currentDir={voucherDir}
                        onSort={onVoucherSort}
                      />
                    </th>
                    <th>
                      <SortHeader
                        column="agreement_number"
                        label="Agreement"
                        currentSort={voucherSort}
                        currentDir={voucherDir}
                        onSort={onVoucherSort}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedVouchers.map((v) => (
                    <tr key={v.id}>
                      <td className={styles.num}>{formatMoney(v.total)}</td>
                      <td>{v.date_range || '—'}</td>
                      <td className={styles.muted}>{formatDate(v.settlement_date)}</td>
                      <td>{v.location_name || '—'}</td>
                      <td>
                        <span className={styles.subMono} title={v.agreement_number || ''}>
                          {v.agreement_number || '—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <StudentEditorModal
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setPendingFamilyId(null);
        }}
        mode="edit"
        record={student}
        labels={labels}
        familiesLookup={familiesLookup}
        onLabelsRefresh={refreshLabelsOnly}
        onSaved={() => {
          void load();
          void refreshFamiliesLookup();
        }}
        onAddNewFamily={() => setFamilyModalOpen(true)}
        flash={flash}
        zIndex={familyModalOpen ? 1990 : 2000}
        familyModalOpen={familyModalOpen}
        selectFamilyId={pendingFamilyId}
      />

      <FamilyEditorModal
        open={familyModalOpen}
        onClose={() => setFamilyModalOpen(false)}
        mode="create"
        record={null}
        labels={labels}
        onLabelsRefresh={refreshLabelsOnly}
        onSaved={(saved) => {
          setPendingFamilyId(saved.id);
          setFamilyModalOpen(false);
          void refreshFamiliesLookup();
        }}
        flash={flash}
        zIndex={2100}
        closeOnBackdrop={!editorOpen}
        closeOnEscape={!editorOpen}
      />
    </div>
  );
}
